import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { PermissionItemProps } from "apis/permission";
import { PaginationProps } from "utils/types/API";

interface PermissionTreeItemProps extends PermissionItemProps {
    children: PermissionTreeItemProps[]
}
export interface PermissionState {
    loading: boolean;
    list: {
        data: PermissionItemProps[];
        pagination?: PaginationProps;
    };
    tree: {
        data: PermissionTreeItemProps[];
        options: PermissionTreeItemProps[];
        expandedIds: number[];
        selected?: PermissionTreeItemProps;
    },
    item: {
        data?: PermissionItemProps;
    };
}

const initialState: PermissionState = {
    loading: false,
    list: {
        data: []
    },
    tree: {
        data: [],
        options: [],
        expandedIds: []
    },
    item: {
    }
};

interface IndexProps {
    data: PermissionItemProps[];
    pagination?: PaginationProps;
}
interface TreeProps {
    data: PermissionTreeItemProps[];
}

interface ExpandedProps {
    id: number;
}
interface SelectedProps {
    item?: PermissionTreeItemProps;
}
interface ItemProps {
    data?: PermissionItemProps;
}

const flattenTree = (tree: PermissionTreeItemProps[], level: number = 0): PermissionTreeItemProps[] => {
    let result: PermissionTreeItemProps[] = [];

    for (const node of tree) {
        // tạo prefix "-- " theo level
        const prefix = level > 0 ? "-- ".repeat(level) : "";
        result.push({ ...node, name: prefix + node.name });

        // nếu có children thì xử lý đệ quy
        if (node.children && node.children.length > 0) {
            result = result.concat(flattenTree(node.children, level + 1));
        }
    }

    return result;
}

const removeTree = (tree: PermissionTreeItemProps[], ids: number[]): PermissionTreeItemProps[] => {
    return tree.filter(item => !ids.includes(item.id)).map(item => ({ ...item, children: removeTree(item.children, ids) }))
}
const updateTreeNode = (tree: PermissionTreeItemProps[], node: PermissionTreeItemProps): PermissionTreeItemProps[] => {
    return tree.map(item => {
        if (item.id === node.id) {
            return { ...item, ...node };
        }
        return { ...item, children: updateTreeNode(item.children || [], node) };
    });
};

const addTreeNode = (tree: PermissionTreeItemProps[], node: PermissionTreeItemProps, parentId?: number): PermissionTreeItemProps[] => {
    if (!parentId) {
        return [...tree, node]; // thêm vào root
    }
    return tree.map(item => {
        if (item.id === parentId) {
            return { ...item, children: [...(item.children || []), node] };
        }
        return { ...item, children: addTreeNode(item.children || [], node, parentId) };
    });
};
const findTreeNode = (tree: PermissionTreeItemProps[], id: number): PermissionTreeItemProps | undefined => {
    for (const node of tree) {
        if (node.id === id) return node;
        const found = findTreeNode(node.children || [], id);
        if (found) return found;
    }
    return undefined;
};

export const permissionSlice = createSlice({
    name: "permission",
    initialState,
    reducers: {
        loading: (state) => {
            state.loading = true;
        },
        endLoading: (state) => {
            state.loading = false;
        },
        index: (state, action: PayloadAction<IndexProps>) => {
            state.loading = false;
            state.list.data = action.payload.data;
            state.list.pagination = action.payload.pagination;
        },
        tree: (state, action: PayloadAction<TreeProps>) => {
            state.loading = false;
            state.tree.data = action.payload.data;
            state.tree.options = flattenTree(action.payload.data);
            state.tree.expandedIds = [];
            state.tree.selected = undefined;
        },
        onExpanded: (state, action: PayloadAction<ExpandedProps>) => {
            const id = action.payload.id;
            state.tree.expandedIds = state.tree.expandedIds.includes(id) ? state.tree.expandedIds.filter(_id => _id !== id) : [...state.tree.expandedIds, id];
        },
        onSelected: (state, action: PayloadAction<SelectedProps>) => {
            state.tree.selected = action.payload.item;
        },
        find: (state, action: PayloadAction<ItemProps>) => {
            state.loading = false;
            state.item.data = action.payload.data;
        },
        create: (state, action: PayloadAction<ItemProps>) => {
            state.loading = false;
            state.item.data = action.payload.data;
            if (action.payload.data) {
                const parentId = action.payload.data.parent?.id;
                state.tree.data = addTreeNode(
                    state.tree.data,
                    { ...action.payload.data, children: [] },
                    parentId
                );
                state.tree.options = flattenTree(state.tree.data);
                if (state.tree.selected?.id) {
                    state.tree.selected = findTreeNode(state.tree.data, state.tree.selected.id);
                }
            }
        },
        update: (state, action: PayloadAction<ItemProps>) => {
            state.loading = false;
            state.item.data = action.payload.data;
            state.list.data = state.list.data.map(item => item.id === action.payload.data?.id ? action.payload.data : item);
            if (action.payload.data) {
                state.tree.data = updateTreeNode(
                    state.tree.data,
                    { ...action.payload.data, children: [] }
                );
                state.tree.options = flattenTree(state.tree.data);
                if (state.tree.selected?.id) {
                    state.tree.selected = findTreeNode(state.tree.data, state.tree.selected.id);
                }
            }
        },
        remove: (state, action: PayloadAction<{ ids: number[] }>) => {
            state.loading = false;
            const data = removeTree(state.tree.data, action.payload.ids);
            state.tree.data = data;
            state.tree.options = flattenTree(data);
            if (state.tree.selected?.id) {
                state.tree.selected = findTreeNode(state.tree.data, state.tree.selected.id);
            }
        },
    },
});

export const { loading, endLoading, index, tree, onExpanded, onSelected, find, create, update, remove } = permissionSlice.actions;
export default permissionSlice.reducer;
